import type { SxProps } from '@mui/system';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ReplayIcon from '@mui/icons-material/Replay';
import BuildIcon from '@mui/icons-material/Build';
import BugReportIcon from '@mui/icons-material/BugReport';
import ArticleIcon from '@mui/icons-material/Article';
import AdbIcon from '@mui/icons-material/Adb';
import ScreenShareIcon from '@mui/icons-material/ScreenShare';
import SurroundSoundIcon from '@mui/icons-material/SurroundSound';
import BlockIcon from '@mui/icons-material/Block';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import SettingsIcon from '@mui/icons-material/Settings';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import CampaignIcon from '@mui/icons-material/Campaign';

export type TenantCategory = 'tgmaxx' | 'non-tgmaxx';

export type DeviceAction = {
  label: string;
  color?: string;
  Icon: any;
  command?: string;
  actionType?: 'push-launcher-config' | 'push-clean-app-data' | 'push-message-config';
  tenants?: TenantCategory[];
};

export const ACTIONS: DeviceAction[] = [
  { label: 'Lock', color: '#fb9e0aff', Icon: LockIcon, command: 'LOCK' },
  { label: 'Unlock', color: '#2ec4ff', Icon: LockOpenIcon, command: 'UNLOCK' },
  { label: 'Reboot', color: '#ff9aa2', Icon: RestartAltIcon, command: 'REBOOT' },
  { label: 'Factory Reset', color: '#45b39d', Icon: DeleteForeverIcon, command: 'RESET' },
  { label: 'Clean Data', color: '#a66bd8', Icon: DeleteSweepIcon, command: 'CLEAN_APPS', tenants: ['non-tgmaxx'] },
  { label: 'Clean Cache', color: '#7f7fff', Icon: ReplayIcon, command: 'CLEAN_CACHE', tenants: ['non-tgmaxx'] },
  { label: 'Restart Agent', color: '#ffb3d9', Icon: BuildIcon, command: 'RESTART_AGENT', tenants: ['non-tgmaxx'] },
  // { label: 'Diagnosis', color: '#32d1b6', Icon: BugReportIcon },
  { label: 'Logcat', color: '#f7d060', Icon: ArticleIcon, tenants: ['non-tgmaxx'] },
  { label: 'Launcher Text', color: '#46ff68ff', Icon: TextFieldsIcon, tenants: ['tgmaxx'] },
  { label: 'Launcher Config', color: '#7ab6ff', Icon: SettingsIcon, actionType: 'push-launcher-config', tenants: ['tgmaxx'] },
  { label: 'Clean App Data', color: '#65c3ba', Icon: CleaningServicesIcon, actionType: 'push-clean-app-data', tenants: ['tgmaxx'] },
  { label: 'Push Message', color: '#f1a55e', Icon: CampaignIcon, actionType: 'push-message-config', tenants: ['tgmaxx'] },
  // { label: 'Remote ADB', color: '#d4a017', Icon: AdbIcon },
  { label: 'Remote Screen', Icon: ScreenShareIcon, command: 'REMOTE_SCREEN' },
  { label: 'Preferences', color: '#a6c55fff', Icon: SettingsIcon, command: 'SET_PREFERENCE', tenants: ['non-tgmaxx'] },
  // { label: 'Audio / Video Logs', color: '#20c997', Icon: SurroundSoundIcon },
  { label: 'Deactivate', color: '#ff6b6b', Icon: BlockIcon, command: 'DEACTIVATE', tenants: ['non-tgmaxx'] },
];

export const isTGMaxxTenant = (source?: string): boolean =>
  (source ?? '').toLowerCase() === 'tgmaxx';

export const getActionsForTenant = (source?: string): DeviceAction[] => {
  const tenant: TenantCategory = isTGMaxxTenant(source) ? 'tgmaxx' : 'non-tgmaxx';
  const src = (source ?? '').toLowerCase();
  const excludeRemoteFor = new Set(['tgstreamtl4kbr', 'tgstreamtest']);
  return ACTIONS.filter((action) => {
    // First, tenant-based filtering
    if (action.tenants && !action.tenants.includes(tenant)) return false;
    // Then, tenant-specific exclusion: hide Remote Screen for specific TGStream test tenants
    if (excludeRemoteFor.has(src) && action.command === 'REMOTE_SCREEN') return false;
    return true;
  });
};

export const tileSx = (bg: string): SxProps => ({
  backgroundColor: bg,
  color: '#fff',
  height: 56,
  fontSize: '0.9rem',
  textTransform: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: 0.4,
  justifyContent: 'flex-start',
  px: 1.5,
  '&:hover': { filter: 'brightness(0.95)' },
});

export default ACTIONS;
